// /app/frontend/src/pages/admin/AdminRoomsPage.jsx
// Admin Rooms Management - View rooms, edit settings, manage prompts

import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  Settings,
  ChevronRight,
  RefreshCw,
  Check,
  X,
  Edit2,
  Save,
  Database
} from 'lucide-react';
import { toast } from 'sonner';
import apiClient from "../../api/client";
import { AuthContext } from '../../App';

const API = window.location.origin;

export default function AdminRoomsPage() {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rooms, setRooms] = useState([]);
  const [editingRoom, setEditingRoom] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', description: '' });
  const [seeding, setSeeding] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchRooms = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/admin/content/rooms`, { headers });
      setRooms(res.data);
    } catch (err) {
      console.error('Failed to fetch rooms:', err);
      toast.error('Failed to load rooms');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await apiClient.post(`/admin/content/seed`, {}, { headers });
      const data = res.data;
      toast.success(
        `Seeded: ${data.rooms_created} rooms, ${data.prompts_created} prompts created`
      );
      fetchRooms();
    } catch (err) {
      toast.error('Seed failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSeeding(false);
    }
  };

  const handleToggleActive = async (room) => {
    const roomKey = room.slug || room.room_key;
    try {
      await apiClient.patch(`/admin/content/rooms/${roomKey}`,
        { is_active: !room.is_active },
        { headers }
      );
      toast.success(`Room ${room.is_active ? 'deactivated' : 'activated'}`);
      fetchRooms();
    } catch (err) {
      toast.error('Failed to update room');
    }
  };

  const handleEditStart = (room) => {
    setEditingRoom(room.slug || room.room_key);
    setEditForm({ name: room.name, description: room.description || '' });
  };

  const handleEditCancel = () => {
    setEditingRoom(null);
    setEditForm({ name: '', description: '' });
  };

  const handleEditSave = async (room) => {
    const roomKey = room.slug || room.room_key;
    try {
      await apiClient.patch(`/admin/content/rooms/${roomKey}`,
        { name: editForm.name, description: editForm.description },
        { headers }
      );
      toast.success('Room updated');
      setEditingRoom(null);
      fetchRooms();
    } catch (err) {
      toast.error('Failed to update room');
    }
  };

  return (
    <div className="min-h-screen surface-page p-4 md:p-6" data-testid="admin-rooms-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
              Room Management
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Manage rooms and their daily prompts
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSeed}
              disabled={seeding}
              className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-800/50 transition disabled:opacity-50"
              data-testid="seed-btn"
            >
              <Database className="w-4 h-4" />
              {seeding ? 'Seeding...' : 'Seed Defaults'}
            </button>
            <button
              onClick={fetchRooms}
              className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 transition"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Rooms List */}
        <div className="space-y-3">
          {loading ? (
            <div className="p-8 text-center text-gray-500">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading rooms...
            </div>
          ) : rooms.length === 0 ? (
            <div className="p-8 text-center surface-card rounded-xl">
              <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500 mb-4">No rooms found. Seed default content to get started.</p>
              <button
                onClick={handleSeed}
                disabled={seeding}
                className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition"
              >
                Seed Default Rooms & Prompts
              </button>
            </div>
          ) : (
            rooms.map((room) => {
              const roomKey = room.slug || room.room_key;
              const isEditing = editingRoom === roomKey;

              return (
                <div
                  key={roomKey}
                  className="surface-card rounded-xl p-4 shadow-sm"
                  data-testid={`room-card-${roomKey}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center gap-3">
                    {/* Room Info */}
                    <div className="flex-1 min-w-0">
                      {isEditing ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            className="w-full px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900"
                            placeholder="Room name"
                          />
                          <textarea
                            value={editForm.description}
                            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                            className="w-full px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900"
                            placeholder="Description"
                            rows={2}
                          />
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                              {room.name}
                            </h3>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-slate-800 text-gray-500">
                              {roomKey}
                            </span>
                            {room.is_legacy_slug && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400" data-testid={`legacy-badge-${roomKey}`}>
                                Legacy → {room.legacy_redirect_to || 'N/A'}
                              </span>
                            )}
                            {room.is_hidden && !room.is_legacy_slug && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-gray-400">
                                Hidden
                              </span>
                            )}
                            {!room.is_active && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600">
                                Inactive
                              </span>
                            )}
                            {room.is_test && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                                Test
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                            {room.description || 'No description'}
                          </p>
                        </>
                      )}
                    </div>

                    {/* Prompt Info */}
                    <div className="flex items-center gap-4 text-sm">
                      <div className="text-center">
                        <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          {room.prompt_count || 0}
                        </div>
                        <div className="text-xs text-gray-500">prompts</div>
                      </div>
                      {room.active_prompt_set && (
                        <div className="text-center">
                          <div className="text-lg font-bold text-purple-600 dark:text-purple-400">
                            {room.active_prompt_set.cycle_length}
                          </div>
                          <div className="text-xs text-gray-500">day cycle</div>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {isEditing ? (
                        <>
                          <button
                            onClick={() => handleEditSave(room)}
                            className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 hover:bg-green-200 dark:hover:bg-green-800/50"
                            title="Save"
                          >
                            <Save className="w-4 h-4" />
                          </button>
                          <button
                            onClick={handleEditCancel}
                            className="p-2 rounded-lg bg-gray-100 dark:bg-slate-800 text-gray-500 hover:bg-gray-200 dark:hover:bg-slate-700"
                            title="Cancel"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEditStart(room)}
                            className="p-2 rounded-lg bg-gray-100 dark:bg-slate-800 text-gray-500 hover:bg-gray-200 dark:hover:bg-slate-700"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleToggleActive(room)}
                            className={`p-2 rounded-lg ${
                              room.is_active
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-600'
                                : 'bg-red-100 dark:bg-red-900/30 text-red-600'
                            } hover:opacity-80`}
                            title={room.is_active ? 'Deactivate' : 'Activate'}
                          >
                            {room.is_active ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => navigate(`/admin/rooms/prompts?room=${roomKey}`)}
                            className="flex items-center gap-1 px-3 py-2 rounded-lg bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-800/50"
                            data-testid={`manage-prompts-${roomKey}`}
                          >
                            <Settings className="w-4 h-4" />
                            <span className="hidden sm:inline">Prompts</span>
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
